import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { Checkbox, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import InputLocation from '../../../../react-components/InputLocation';
import Text from '../../../../react-components/Text';

export interface LocationInputsProps {
  start?: Location;
  end?: Location;
}

interface VehiclesLocationInputsProps {
  vehiclesLocation?: LocationInputsProps;
  focusedBorderColor?: string;
  onChange: (vehiclesLocation: LocationInputsProps) => void;
}

export const VehiclesLocationInputs = ({
  vehiclesLocation,
  focusedBorderColor,
  onChange,
}: VehiclesLocationInputsProps) => {
  const intl = useIntl();
  const [isCircular, setIsCircular] = useState<boolean>(false);

  useEffect(() => {
    if (!vehiclesLocation?.start && !vehiclesLocation?.end) {
      setIsCircular(true);
      return;
    }
    if (vehiclesLocation.start && vehiclesLocation.end) {
      const areEqual =
        vehiclesLocation.start.lat === vehiclesLocation.end.lat &&
        vehiclesLocation.start.lng === vehiclesLocation.end.lng;
      setIsCircular(areEqual);
    } else {
      setIsCircular(false);
    }
  }, [vehiclesLocation]);

  return (
    <Grid container direction="column">
      <Grid container direction="row" alignItems="center">
        <Text intlId={`company.onboarding.vehicles.location.start`} variant="label" />
        <Text style={{ margin: `0 4px` }} variant="label">
          /
        </Text>
        <Text
          intlId={`company.onboarding.vehicles.location.location_is_circular`}
          variant="label"
          style={{ marginRight: `4px` }}
        />
        <Checkbox
          disabled={!vehiclesLocation?.start}
          checked={isCircular}
          onChange={(e: any) => {
            if (!vehiclesLocation?.start && !vehiclesLocation?.end) {
              return;
            }

            if (!e.target.checked) {
              onChange({
                ...vehiclesLocation,
                end: undefined,
              });
              return;
            }

            if (vehiclesLocation.start) {
              onChange({
                ...vehiclesLocation,
                end: vehiclesLocation.start,
              });
              return;
            }

            if (vehiclesLocation.end) {
              onChange({
                ...vehiclesLocation,
                start: vehiclesLocation.end,
              });
            }
          }}
        />
      </Grid>

      <VSpacer small />

      <Grid item container direction="row">
        <InputLocation
          fullWidth
          inputProps={{
            styles: {},
            size: `medium`,
            variant: `outlined`,
            placeholder: `${intl.formatMessage({ id: `company.onboarding.vehicles.location.start` })}...`,
          }}
          emptyIntlId="no_results"
          focusedBorderColor={focusedBorderColor}
          fieldValue={vehiclesLocation?.start ?? undefined}
          onUpdate={(location?: Location | null) => {
            onChange({
              start: location ?? undefined,
              ...(isCircular
                ? {
                    end: location ?? undefined,
                  }
                : {}),
            });
          }}
        />
      </Grid>

      {!isCircular && (
        <>
          <VSpacer small />

          <Grid item>
            <InputLocation
              fullWidth
              inputProps={{
                styles: {},
                size: `medium`,
                variant: `outlined`,
                placeholder: `${intl.formatMessage({ id: `company.onboarding.vehicles.location.end` })}...`,
              }}
              emptyIntlId="no_results"
              focusedBorderColor={focusedBorderColor}
              fieldValue={vehiclesLocation?.end ?? undefined}
              onUpdate={(location?: Location | null) => {
                onChange({ ...vehiclesLocation, end: location ?? undefined });
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
